import { Close } from '@mui/icons-material';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { Button, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { useConfig, useNotification, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useEventBus } from '../../../../hooks/use-event-bus';
import FormsService from '../../../../services/forms-service/forms-service';
import { CircularProgress } from '../../../shared/audio-player/styles';
import PinPad from '../../../shared/numeric-keypad';

import { ContactApi } from '../../../../services/api-client/contact-api';
import { PatientDto, PatientInviteMethod } from '../../../../services/forms-service/forms-service-types';
import { checkPatientData } from './helpers';
import { CenterContent, DialogPin, PinDisplay, YourPinWrapper } from './styles';

const messages = {
  default: { text: 'Please enter the PIN you would like to use', isValid: true },
  confirmMessage: { text: 'Please re-enter the PIN', isValid: true },
  invalidPin: { text: 'Your PIN did not match.', isValid: false },
  tooShortMessage: { text: 'Your PIN must be at least 4 characters', isValid: false },
  additionalSecurityMessage: {
    text: 'For additional security, do not save your log in credentials in your browser.',
    isValid: true
  },
  yourPinMessage: { text: 'Your PIN for accessing additional patient forms.', isValid: true }
};

export default function ModalFillOnThisDevice() {
  const [open, setOpen] = useState(false);
  const [hasPin, setHasPin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [patient, setPatient] = useState<PatientDto | null>(null);

  const [pinType, setPinType] = useState<'confirm' | 'default'>('default');
  const [messageToDisplay, setMessageToDisplay] = useState<keyof typeof messages>('default');

  const { listen, publish } = useEventBus();
  const { config } = useConfig();
  const oidc = useOidcWithAdmin();
  const { showError } = useNotification();

  const formsService = useMemo(() => FormsService.getInstance(config, oidc.oidcService), [config, oidc]);
  const contactApi = useMemo(() => ContactApi.getInstance(), [ContactApi]);

  const initiateFillOnThisDevice = useCallback(
    async (patientId?: number, patientData?: PatientDto) => {
      try {
        setIsLoading(true);
        const patient = patientData || (await contactApi.getContactDetailsById(patientId!));

        checkPatientData(patient, publish, 'or');

        setPatient(patient);
        const userData = await formsService.getUser();

        if (userData.pin) {
          setHasPin(true);
          setPin(String(userData.pin));
        }

        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching user data:', error);
        setIsLoading(false);
      }
    },
    [config, contactApi, formsService, publish]
  );

  useEffect(() => {
    const handleEvent = async (event: any) => {
      const data = event.data as { type: string; patientId: number; patient?: PatientDto };

      if (data.type === 'fill-on-this-device') {
        setHasPin(false);
        setOpen(true);
        setIsLoading(true);
        setShowPin(false);
        setPinType('default');
        setMessageToDisplay('default');
        setPatient(null);

        initiateFillOnThisDevice(data.patientId!, data.patient);
      }
    };

    return listen('formsDialog', handleEvent);
  }, [listen, initiateFillOnThisDevice]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleEnter = async (newPin: string) => {
    if (newPin.length < 4) {
      setMessageToDisplay('tooShortMessage');
      return;
    }

    if (pinType === 'default') {
      setPin(newPin);
      setPinType('confirm');
      setMessageToDisplay('confirmMessage');
    } else if (pinType === 'confirm') {
      if (newPin === pin) {
        try {
          setIsLoading(true);
          await formsService.addOrUpdatePin(newPin);
          setHasPin(true);
        } catch {
          showError('Error setting pin');
        }
        setIsLoading(false);
      } else {
        setMessageToDisplay('invalidPin');
      }
    }
  };

  const continueToFormsClick = async () => {
    const win = window.open();

    try {
      const { token } = await formsService.invite(patient!.id, PatientInviteMethod.SignInAs);
      win!.location = token!;
      publish({ messageType: 'logout' });
    } catch (error) {
      showError('Error inviting patient');
    }

    handleClose();
  };

  return (
    <DialogPin open={open} onClose={handleClose} style={hasPin || isLoading ? { minHeight: '400px' } : undefined}>
      {isLoading && (
        <DialogContent>
          <CenterContent>
            <CircularProgress />
          </CenterContent>
        </DialogContent>
      )}
      {!isLoading && hasPin && (
        <>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
            Going to {patient?.firstName} {patient?.lastName}’s Forms
            <IconButton sx={{ ml: 'auto' }} onClick={handleClose}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            To ensure that only authorized office employees can access sensitive data, this will log you out of
            RevenueWell.
            <YourPinWrapper>
              <i>{messages.additionalSecurityMessage.text}</i>
              <br />
              <div>{messages.yourPinMessage.text}</div>
              <PinDisplay>
                {showPin && <>{pin}</>}
                {!showPin && <>....</>}
              </PinDisplay>
            </YourPinWrapper>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                setShowPin(!showPin);
              }}
              color="secondary"
              startIcon={showPin ? <RemoveRedEyeOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
            >
              {!showPin ? 'Show PIN' : 'Hide PIN'}
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => setHasPin(false)}
              color="secondary"
              startIcon={<EditOutlinedIcon />}
            >
              Change PIN
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={continueToFormsClick}
              endIcon={<ArrowForwardOutlinedIcon />}
            >
              Continue to Forms
            </Button>
          </DialogActions>
        </>
      )}
      {!isLoading && !hasPin && (
        <>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
            Create your PIN
            <IconButton sx={{ ml: 'auto' }} onClick={handleClose}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            To ensure that only authorized office employees can access sensitive data, please create a PIN to return to
            Assignments after your patient is done filling out their forms.
            <br />
            <PinPad
              handleEnter={handleEnter}
              message={messages[messageToDisplay].text}
              valid={messages[messageToDisplay].isValid}
            />
          </DialogContent>
        </>
      )}
    </DialogPin>
  );
}
